import * as z from "zod";

export const reclaimAccessModes = ["trial", "free"] as const;
export type ReclaimAccessMode = (typeof reclaimAccessModes)[number];

export const disputesAccessOverrides = [
  "default",
  "trial",
  "free",
  "paid",
] as const;
export type DisputesAccessOverride = (typeof disputesAccessOverrides)[number];
export const DisputesAccessOverride = disputesAccessOverrides;

export const reclaimAccessSources = [
  "intake_modal",
  "placeholder_landing",
  "shopify_reclaim_install",
] as const;
export type ReclaimAccessSource = (typeof reclaimAccessSources)[number];

export const reclaimActivationEntrySources = [
  "intake_modal",
  "insights_page_cta",
  "shopify_reclaim_install",
] as const;
export type ReclaimActivationEntrySource =
  (typeof reclaimActivationEntrySources)[number];

export function getReclaimActivationSourceForEntrySource(
  entrySource: ReclaimActivationEntrySource,
): ReclaimAccessSource {
  switch (entrySource) {
    case "insights_page_cta":
      return "placeholder_landing";
    case "shopify_reclaim_install":
      return "shopify_reclaim_install";
    default:
      return "intake_modal";
  }
}

export const reclaimSourceRulePricingModes = ["default", "custom"] as const;
export type ReclaimSourceRulePricingMode =
  (typeof reclaimSourceRulePricingModes)[number];

export interface ReclaimPricingTerms {
  successFeePercentage: number;
  minFeeDollars: number;
}

export interface ReclaimSourceRule {
  accessMode: DisputesAccessOverride;
  pricingMode: ReclaimSourceRulePricingMode;
  successFeePercentage?: number;
  minFeeDollars?: number;
}

export type ReclaimSourceRules = Record<ReclaimAccessSource, ReclaimSourceRule>;

export interface ReclaimAccessDefaults {
  accessMode: ReclaimAccessMode;
  trialMonths: number;
  successFeePercentage: number;
  minFeeDollars: number;
  sourceRules: ReclaimSourceRules;
}

export interface ReclaimResolvedTerms extends ReclaimPricingTerms {
  accessMode: ReclaimAccessMode | "paid";
  trialMonths: number;
}

export interface ReclaimPricingOverride extends ReclaimPricingTerms {
  source?: string;
  appliedAt?: Date;
}

export interface ReclaimSourceMaterialization {
  accessOverride: DisputesAccessOverride;
  pricingOverride: ReclaimPricingOverride;
}

export const RECLAIM_PARTNER_REFERRAL_PRICING_SOURCE_PREFIX =
  "partner_referral:";

export interface ReclaimCommercialTermsDisputes {
  accessOverride?: DisputesAccessOverride | null;
  freeAccessEnabled?: boolean | null;
  pricingOverride?: {
    successFeePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
  billing?: {
    feePercentage?: number | null;
    minFeeDollars?: number | null;
  } | null;
}

export const DEFAULT_RECLAIM_TRIAL_MONTHS = 6;
export const DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE = 25;
export const DEFAULT_RECLAIM_MIN_FEE_DOLLARS = 0;

export const DEFAULT_RECLAIM_SOURCE_RULES: ReclaimSourceRules = {
  intake_modal: { accessMode: "free", pricingMode: "default" },
  placeholder_landing: { accessMode: "paid", pricingMode: "default" },
  shopify_reclaim_install: { accessMode: "default", pricingMode: "default" },
};

export const DEFAULT_RECLAIM_ACCESS_DEFAULTS: ReclaimAccessDefaults = {
  accessMode: "trial",
  trialMonths: DEFAULT_RECLAIM_TRIAL_MONTHS,
  successFeePercentage: DEFAULT_RECLAIM_SUCCESS_FEE_PERCENTAGE,
  minFeeDollars: DEFAULT_RECLAIM_MIN_FEE_DOLLARS,
  sourceRules: DEFAULT_RECLAIM_SOURCE_RULES,
};

export const reclaimPricingTermsSchema = z.object({
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
});

export const reclaimSourceRuleSchema = z.object({
  accessMode: z.enum(disputesAccessOverrides),
  pricingMode: z.enum(reclaimSourceRulePricingModes),
  successFeePercentage: z.number().min(0).max(100).optional(),
  minFeeDollars: z.number().min(0).optional(),
});

export const reclaimSourceRulesSchema = z.object({
  intake_modal: reclaimSourceRuleSchema,
  placeholder_landing: reclaimSourceRuleSchema,
  shopify_reclaim_install: reclaimSourceRuleSchema,
});

export const reclaimAccessDefaultsSchema = z.object({
  accessMode: z.enum(reclaimAccessModes),
  trialMonths: z.number().int().min(1).max(120),
  successFeePercentage: z.number().min(0).max(100),
  minFeeDollars: z.number().min(0),
  sourceRules: reclaimSourceRulesSchema,
});

export function normalizeReclaimAccessDefaults(
  input: unknown,
): ReclaimAccessDefaults {
  const overrides =
    input && typeof input === "object" && !Array.isArray(input)
      ? Object.fromEntries(Object.entries(input))
      : {};
  const sourceRulesInput: Record<string, unknown> =
    overrides.sourceRules &&
    typeof overrides.sourceRules === "object" &&
    !Array.isArray(overrides.sourceRules)
      ? (overrides.sourceRules as Record<string, unknown>)
      : {};
  const successFeePercentage =
    typeof overrides.successFeePercentage === "number"
      ? overrides.successFeePercentage
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.successFeePercentage;
  const minFeeDollars =
    typeof overrides.minFeeDollars === "number"
      ? overrides.minFeeDollars
      : DEFAULT_RECLAIM_ACCESS_DEFAULTS.minFeeDollars;
  const mergedDefaults = {
    ...DEFAULT_RECLAIM_ACCESS_DEFAULTS,
    ...overrides,
    sourceRules: reclaimAccessSources.reduce<Partial<ReclaimSourceRules>>(
      (sourceRules, source) => {
        const rawSourceRule = sourceRulesInput[source];
        const sourceRuleInput =
          rawSourceRule &&
          typeof rawSourceRule === "object" &&
          !Array.isArray(rawSourceRule)
            ? Object.fromEntries(Object.entries(rawSourceRule))
            : {};
        const rule = {
          ...DEFAULT_RECLAIM_SOURCE_RULES[source],
          ...sourceRuleInput,
        };
        sourceRules[source] =
          rule.pricingMode === "custom"
            ? {
                ...rule,
                successFeePercentage:
                  rule.successFeePercentage ?? successFeePercentage,
                minFeeDollars: rule.minFeeDollars ?? minFeeDollars,
              }
            : { accessMode: rule.accessMode, pricingMode: "default" };
        return sourceRules;
      },
      {},
    ),
  };
  return reclaimAccessDefaultsSchema.parse(mergedDefaults);
}

export function resolveReclaimSourceRule(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
): ReclaimSourceRule {
  return (
    accessDefaults.sourceRules?.[source] ?? DEFAULT_RECLAIM_SOURCE_RULES[source]
  );
}

export function resolveReclaimTerms(
  accessDefaults: ReclaimAccessDefaults,
  source?: ReclaimAccessSource,
): ReclaimResolvedTerms {
  const sourceRule = source
    ? resolveReclaimSourceRule(accessDefaults, source)
    : undefined;
  const accessMode =
    sourceRule && sourceRule.accessMode !== "default"
      ? sourceRule.accessMode
      : accessDefaults.accessMode;
  return {
    accessMode,
    trialMonths: accessDefaults.trialMonths,
    ...resolveReclaimPricingTerms(accessDefaults, sourceRule),
  };
}

export function resolveReclaimCommercialTerms({
  accessDefaults,
  disputes,
  source,
}: {
  accessDefaults: ReclaimAccessDefaults;
  disputes?: ReclaimCommercialTermsDisputes | null;
  source?: ReclaimAccessSource;
}): ReclaimResolvedTerms {
  const sourceTerms = resolveReclaimTerms(accessDefaults, source);

  return {
    accessMode: resolveReclaimCommercialAccessMode(disputes, sourceTerms),
    trialMonths: sourceTerms.trialMonths,
    ...resolveReclaimCommercialPricing(disputes, sourceTerms),
  };
}

export function resolveReclaimPricingTerms(
  accessDefaults: ReclaimAccessDefaults,
  sourceRule?: ReclaimSourceRule,
): ReclaimPricingTerms {
  if (sourceRule?.pricingMode === "custom") {
    return reclaimPricingTermsSchema.parse({
      successFeePercentage:
        sourceRule.successFeePercentage ?? accessDefaults.successFeePercentage,
      minFeeDollars: sourceRule.minFeeDollars ?? accessDefaults.minFeeDollars,
    });
  }
  return {
    successFeePercentage: accessDefaults.successFeePercentage,
    minFeeDollars: accessDefaults.minFeeDollars,
  };
}

export function getReclaimSourceMaterialization(
  accessDefaults: ReclaimAccessDefaults,
  source: ReclaimAccessSource,
  appliedAt: Date,
): ReclaimSourceMaterialization {
  // Always materialize concrete access and pricing values onto the merchant
  // at flow time. When the source rule says "default", resolve through to
  // current globals so the team carries a self-contained, frozen record of
  // the terms in effect at activation. Admin global edits will only affect
  // future activations.
  const sourceRule = resolveReclaimSourceRule(accessDefaults, source);
  const terms = resolveReclaimTerms(accessDefaults, source);

  return {
    accessOverride:
      sourceRule.accessMode === "default"
        ? accessDefaults.accessMode
        : sourceRule.accessMode,
    pricingOverride: {
      successFeePercentage: terms.successFeePercentage,
      // Commercial policy: no minimum, ever.
      minFeeDollars: 0,
      source,
      appliedAt,
    },
  };
}

export function getReclaimPartnerReferralFreeAccessMaterialization({
  appliedAt,
  referralCode,
}: {
  appliedAt: Date;
  referralCode: string;
}): ReclaimSourceMaterialization {
  const normalizedReferralCode = referralCode.trim().toUpperCase();
  return {
    accessOverride: "free",
    pricingOverride: {
      successFeePercentage: 0,
      minFeeDollars: 0,
      source: `${RECLAIM_PARTNER_REFERRAL_PRICING_SOURCE_PREFIX}${normalizedReferralCode}`,
      appliedAt,
    },
  };
}

function resolveReclaimCommercialAccessMode(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimResolvedTerms["accessMode"] {
  if (disputes?.accessOverride === "paid") {
    return "paid";
  }
  if (
    disputes?.accessOverride === "free" ||
    disputes?.freeAccessEnabled === true
  ) {
    return "free";
  }
  if (disputes?.accessOverride === "trial") {
    return "trial";
  }
  return sourceTerms.accessMode;
}

function resolveReclaimCommercialPricing(
  disputes: ReclaimCommercialTermsDisputes | null | undefined,
  sourceTerms: ReclaimResolvedTerms,
): ReclaimPricingTerms {
  // Commercial policy: Reclaim has no dollar minimum, ever. Legacy team docs
  // and source rules may still carry a minFeeDollars value; ignore it here
  // so neither billing math (Math.max(min, ...)) nor pitch copy can apply
  // one.
  if (disputes?.pricingOverride?.successFeePercentage != null) {
    return {
      successFeePercentage: disputes.pricingOverride.successFeePercentage,
      minFeeDollars: 0,
    };
  }

  if (disputes?.billing?.feePercentage != null) {
    return {
      successFeePercentage: disputes.billing.feePercentage,
      minFeeDollars: 0,
    };
  }

  return {
    successFeePercentage: sourceTerms.successFeePercentage,
    minFeeDollars: 0,
  };
}
